import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Chip, IconButton, Stack, Typography } from "@mui/material";
import { Edit, Add } from "@mui/icons-material";
import { ColorsConstants } from "../../cssUtils/constants";
import AppTable from "../../common/app-table";
import TableSearchInput from "../TableSearchInput";
import Filter from "../Filter";
import GoalPlanFormDialog from "./GoalPlanFormDialog";
import GoalPlanDetailView from "./GoalPlanDetailView";
import ButtonOne from "../../common/normal-button";
import { useAuth } from "../../hooks/AuthProvider";
import { MFD_GOAL_PLANS_ENDPOINT } from "../../config/urls";
import { formatWithTzLabel } from "../../utils/dateUtils";

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = ["Open", "In Progress", "Converted", "Not Interested"];

const FILTER_FIELDS = [
  { label: "Status", type: "single_select_list", values: STATUS_OPTIONS },
];

const STATUS_CHIP_COLOR = {
  Open: "info",
  "In Progress": "warning",
  Converted: "success",
  "Not Interested": "default",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtINR = (n) => {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
};

const fmtYears = (n) =>
  n == null || n === "" ? "—" : `${n} year${Number(n) === 1 ? "" : "s"}`;

const StatusCell = ({ row }) => (
  <Chip
    variant="outlined"
    size="small"
    label={row.status}
    color={STATUS_CHIP_COLOR[row.status] ?? "default"}
  />
);

// ─── Columns ──────────────────────────────────────────────────────────────────

const getColumns = (onEdit) => [
  { id: "client_name", label: "Client Name", minWidth: 160 },
  { id: "pan", label: "PAN", minWidth: 130 },
  { id: "client_code", label: "Client Code", minWidth: 120 },
  { id: "goal", label: "Goal", minWidth: 160 },
  {
    id: "target_amount",
    label: "Target Amount",
    minWidth: 140,
    render: (row) => fmtINR(row.target_amount),
  },
  {
    id: "timeline",
    label: "Timeline",
    minWidth: 110,
    render: (row) => fmtYears(row.timeline),
  },
  {
    id: "monthly_sip",
    label: "Monthly SIP",
    minWidth: 140,
    render: (row) => fmtINR(row.monthly_sip),
  },
  {
    id: "lumpsum",
    label: "Lumpsum",
    minWidth: 140,
    render: (row) => fmtINR(row.lumpsum),
  },
  {
    id: "status",
    label: "Status",
    minWidth: 140,
    render: (row) => <StatusCell row={row} />,
  },
  {
    id: "updated_at",
    label: "Updated",
    minWidth: 160,
    render: (row) =>
      row.updated_at ? formatWithTzLabel(row.updated_at) : "—",
  },
  {
    id: "actions",
    label: "Actions",
    minWidth: 80,
    render: (row) => (
      <Stack direction="row">
        <IconButton
          size="medium"
          sx={{ color: ColorsConstants.primaryBlue100 }}
          onClick={() => onEdit(row)}
        >
          <Edit fontSize="medium" />
        </IconButton>
      </Stack>
    ),
  },
];

// ─── Component ────────────────────────────────────────────────────────────────

const GoalPlans = () => {
  const { getValidToken } = useAuth();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rpp, setRpp] = useState(20);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [view, setView] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  // ── Auth ──────────────────────────────────────────────────────────────────

  const getHeaders = useCallback(async () => {
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };
  }, [getValidToken]);

  // ── Fetch ─────────────────────────────────────────────────────────────────

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ limit: rpp, offset: page * rpp });
    if (search.trim()) params.set("search", search.trim());
    filters.forEach(({ field, operator, value }) => {
      params.append(
        field.toLowerCase().replaceAll(" ", "_"),
        `${operator.toLowerCase().replaceAll(" ", "_")}:${value ?? ""}`,
      );
    });
    try {
      const headers = await getHeaders();
      const res = await fetch(`${MFD_GOAL_PLANS_ENDPOINT}?${params}`, {
        headers,
      });
      if (!res.ok) throw new Error("Failed to fetch goal plans");
      const data = await res.json();
      setRows(data.results ?? []);
      setTotal(data.count ?? 0);
    } catch (err) {
      console.error("Error fetching goal plans:", err);
    } finally {
      setLoading(false);
    }
  }, [page, rpp, search, filters, getHeaders]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleView = (row) => setView(row);
  const handleAdd  = () => { setEditData(null); setIsModalOpen(true); };

  // List rows omit phone/email/client FK — fetch the detail so the form has full data
  const handleEdit = async (row) => {
    setIsModalOpen(true);
    setEditData(row);
    try {
      const headers = await getHeaders();
      const res = await fetch(`${MFD_GOAL_PLANS_ENDPOINT}${row.id}/`, { headers });
      if (res.ok) setEditData(await res.json());
    } catch (err) {
      console.error("Error fetching goal plan detail:", err);
    }
  };

  const handleSave = async (form) => {
    const url = editData
      ? `${MFD_GOAL_PLANS_ENDPOINT}${editData.id}/`
      : MFD_GOAL_PLANS_ENDPOINT;
    const method = editData ? "PATCH" : "POST";

    const body = {
      status:        form.status,
      comment:       form.comment,
      goal:          form.goal,
      target_amount: form.target_amount,
      timeline:      form.timeline,
      monthly_sip:   form.monthly_sip,
      lumpsum_today: form.lumpsum,
      ...(form.client_id != null && { client: form.client_id }),
    };

    setSaving(true);
    try {
      const headers = await getHeaders();
      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("Failed to save goal plan");
      setIsModalOpen(false);
      fetchPlans();
    } catch (err) {
      console.error("Error saving goal plan:", err);
    } finally {
      setSaving(false);
    }
  };

  const handlePageChange = (_, newPage) => setPage(newPage);
  const handleRppChange = (e) => {
    setRpp(parseInt(e.target.value, 10));
    setPage(0);
  };
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };
  const handleFilterApply = (newFilters) => {
    setFilters(newFilters);
    setPage(0);
  };

  const columns = useMemo(() => getColumns(handleEdit), []);

  // ── Render ────────────────────────────────────────────────────────────────

  if (view) {
    return <GoalPlanDetailView plan={view} onBack={() => setView(null)} />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, }} >
      <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        justifyContent: "space-between",
        alignItems: { xs: "stretch", md: "center" },
        gap: 2,
        mb: 2,
        }}
        >
        <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          flex: 1,
          alignItems: { xs: "stretch", sm: "center" },
          }}
          >
          <Box sx={{ flex: 1, width: "100%",  maxWidth: { xs: "100%", md: 400 } }}>
            <TableSearchInput
              searchQuery={search}
              onSearchChange={handleSearchChange}
              placeholder="Search by client name, PAN or client code"
            />
          </Box>
          <Filter
            fields={FILTER_FIELDS}
            onApply={handleFilterApply}
            initalFilters={filters}
          />
        </Box>
        <ButtonOne startIcon={<Add />} onClick={handleAdd}
        sx={{
          width: { xs: "100%", sm: "auto" },
          }}
          >Add Goal Plan</ButtonOne>
      </Box>
      <AppTable
        columns={columns}
        rows={rows}
        rowKey="id"
        loading={loading}
        emptyMessage="No goal plans found"
        maxHeight={620}
        hideMainScroll={false}
        containerSx={{ overflowX: "auto" }}
        count={total}
        page={page}
        rowsPerPage={rpp}
        rowsPerPageOptions={[20, 50, 100]}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRppChange}
        onRowClick={handleView}
      />

      <GoalPlanFormDialog
        open={isModalOpen}
        handleClose={() => setIsModalOpen(false)}
        onSave={handleSave}
        editData={editData}
        saving={saving}
      />
    </Box>
  );
};

export default GoalPlans;
